import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const WorkspaceSwitcher = ({ onCreateNew }) => {
  const { workspaces, currentWorkspace, loading, switchWorkspace } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleWorkspaceSelect = (workspace) => {
    switchWorkspace(workspace);
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    if (onCreateNew) {
      onCreateNew();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md animate-pulse bg-primary-500/5 border border-primary-500/10">
        <div className="w-4 h-4 rounded bg-primary-500/15"></div>
        <div className="w-24 h-4 rounded bg-primary-500/10"></div>
      </div>
    );
  }

  // Show create workspace button if no workspaces exist
  if (workspaces.length === 0) {
    return (
      <button
        onClick={handleCreateNew}
        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all cursor-pointer w-full text-sm font-medium bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow-sm hover:from-primary-500 hover:to-green-400"
        aria-label="Create workspace"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        <span>Create Workspace</span>
      </button>
    );
  }

  if (!currentWorkspace) {
    return null;
  }

  // Get first letter of workspace name
  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'W';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all cursor-pointer w-full bg-bg-secondary/60 border border-primary-500/15 hover:border-primary-500/30 hover:bg-primary-500/5"
        aria-label="Switch workspace"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 font-bold text-[11px] bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow-sm">
            {getInitial(currentWorkspace.name)}
          </div>
          <span className="text-sm font-medium truncate text-text-primary">
            {currentWorkspace.name}
          </span>
        </div>
        <svg 
          className={`w-4 h-4 flex-shrink-0 transition-transform text-text-tertiary ${isOpen ? 'rotate-180' : ''}`} 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 left-0 w-64 rounded-lg shadow-lg z-50 py-1 animate-in fade-in slide-in-from-top-2 bg-bg-secondary/95 border border-primary-500/20 backdrop-blur-xl shadow-[0_15px_40px_rgba(3,6,4,0.5),0_0_20px_rgba(50,205,50,0.1)]">
          {/* Current workspace badge */}
          <div className="px-3 py-2 border-b border-primary-500/10">
            <div className="text-xs font-medium uppercase tracking-wide text-text-tertiary">Current Workspace</div>
          </div>

          {/* Workspaces list */}
          <div className="max-h-64 overflow-y-auto">
            {workspaces.map((workspace) => {
              const workspaceId = workspace.id || workspace._id;
              const currentId = currentWorkspace?.id || currentWorkspace?._id;
              const isActive = currentId === workspaceId;
              
              return (
                <button
                  key={workspaceId}
                  onClick={() => handleWorkspaceSelect(workspace)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all ${
                    isActive 
                      ? 'bg-success-50 text-primary-500' 
                      : 'bg-transparent text-text-secondary hover:bg-primary-500/5'
                  }`}
                >
                  <div 
                    className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 font-bold text-[11px] ${
                      isActive 
                        ? 'bg-gradient-to-br from-primary-600 to-primary-500 text-black' 
                        : 'bg-success-50 border border-primary-500/20 text-text-tertiary'
                    }`}
                  >
                    {getInitial(workspace.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{workspace.name}</span>
                      {workspace.is_default && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded text-primary-500 bg-success-50 border border-primary-500/30">
                          DEFAULT
                        </span>
                      )}
                    </div>
                    {workspace.user_role && (
                      <div className="text-xs capitalize text-text-tertiary">{workspace.user_role}</div>
                    )}
                  </div>
                  {isActive && (
                    <svg className="w-4 h-4 flex-shrink-0 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </button>
              );
            })}
          </div>

          {/* Create new workspace */}
          <div className="mt-1 border-t border-primary-500/10">
            <button
              onClick={handleCreateNew}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left transition-all text-text-tertiary hover:text-primary-500 hover:bg-primary-500/5"
            >
              <div className="w-5 h-5 rounded border-2 border-dashed border-primary-500/30 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </div>
              <span className="text-sm font-medium">Create New Workspace</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

WorkspaceSwitcher.propTypes = {
  onCreateNew: PropTypes.func
};

export default WorkspaceSwitcher;
